import { API_BASE_URL } from '@types';
import type {
  SpectralWorkspaceListItem,
  SpectralWorkspaceSnapshot,
} from '../packages/spectral-workspace/types';
import {
  buildEmptyWorkspaceSnapshot,
  isSpectralWorkspaceSnapshot,
} from '../packages/spectral-workspace/utils/buildWorkspaceSnapshot';
import type { SpectralAnalysisListItem } from '../types';

export type SessionGroupResearchUsage = {
  analysisId: string;
  name: string;
  description?: string;
  updatedAt: string;
  sessionCount: number;
};

export type SpectralHubListItem =
  | (SpectralWorkspaceListItem & { kind: 'workspace' })
  | {
      kind: 'legacy';
      analysisId: string;
      name: string;
      description?: string;
      createdAt: string;
      updatedAt: string;
      sessionCount: number;
    };

type SpectralWorkspaceListResponseItem = SpectralAnalysisListItem & {
  analysisType?: string;
  updatedAt?: string;
  snapshot?: SpectralWorkspaceSnapshot;
};

const buildUrl = (path: string) =>
  `${API_BASE_URL}/services/v1/research/spectral-analyses${path}`;

const normalizeSpectralWorkspaceSnapshot = (
  snapshot: SpectralWorkspaceSnapshot
): SpectralWorkspaceSnapshot => ({
  ...snapshot,
  activeSessionGroupId: snapshot.activeSessionGroupId ?? null,
  customSegments: snapshot.customSegments ?? [],
  workspace:
    snapshot.workspace ??
    buildEmptyWorkspaceSnapshot({
      name: snapshot.name,
      author: snapshot.author,
    }).workspace,
});

const parseSnapshotResponse = (parsed: {
  snapshot?: unknown;
  analysisType?: string;
}) => {
  if (!parsed.snapshot || !isSpectralWorkspaceSnapshot(parsed.snapshot)) {
    return null;
  }

  return normalizeSpectralWorkspaceSnapshot(parsed.snapshot);
};

const toListItem = (
  item: SpectralAnalysisListItem & {
    analysisType?: string;
    updatedAt?: string;
  },
  snapshot: SpectralWorkspaceSnapshot
): SpectralWorkspaceListItem => {
  const sessionCount = item.selectedCount ?? 0;

  return {
    analysisId: item.analysisId,
    name: snapshot.name ?? item.name,
    description: snapshot.description ?? item.description,
    createdAt: item.createdAt,
    updatedAt: snapshot.updatedAt ?? item.updatedAt ?? item.createdAt,
    sessionCount,
  };
};

const toLegacyHubItem = (
  item: SpectralWorkspaceListResponseItem
): SpectralHubListItem => ({
  kind: 'legacy',
  analysisId: item.analysisId,
  name: item.name,
  description: item.description,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt ?? item.createdAt,
  sessionCount: item.selectedCount ?? 0,
});

const fetchAllSpectralAnalyses = async (): Promise<
  SpectralWorkspaceListResponseItem[]
> => {
  const response = await fetch(buildUrl(''), {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Lista analisi fallita: HTTP ${response.status} ${message}`
    );
  }

  const parsed = (await response.json()) as SpectralWorkspaceListResponseItem[];

  return Array.isArray(parsed) ? parsed : [];
};

const fetchAnalysisById = async (
  id: string
): Promise<SpectralWorkspaceSnapshot | null> => {
  const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
    method: 'GET',
    credentials: 'include',
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      `Recupero analisi fallito: HTTP ${response.status} ${message}`
    );
  }

  return parseSnapshotResponse(await response.json());
};

const resolveWorkspaceSnapshot = async (
  item: SpectralWorkspaceListResponseItem
): Promise<SpectralWorkspaceSnapshot | null> => {
  if (item.snapshot && isSpectralWorkspaceSnapshot(item.snapshot)) {
    return normalizeSpectralWorkspaceSnapshot(item.snapshot);
  }

  return fetchAnalysisById(item.analysisId);
};

const isSpectralWorkspaceListItem = (item: SpectralWorkspaceListResponseItem) =>
  item.analysisType !== 'spectral_normalization';

const resolveWorkspaceListItem = async (
  item: SpectralWorkspaceListResponseItem
): Promise<SpectralWorkspaceListItem | null> => {
  if (item.analysisType === 'spectral_normalization') {
    return null;
  }

  const snapshot = await resolveWorkspaceSnapshot(item);

  if (!snapshot) {
    return null;
  }

  return toListItem(item, snapshot);
};

const toResearchUsage = (
  item: SpectralWorkspaceListResponseItem,
  snapshot: SpectralWorkspaceSnapshot
): SessionGroupResearchUsage => ({
  analysisId: item.analysisId,
  name: snapshot.name ?? item.name,
  description: snapshot.description ?? item.description,
  updatedAt: snapshot.updatedAt ?? item.updatedAt ?? item.createdAt,
  sessionCount: item.selectedCount ?? 0,
});

export const spectralWorkspaceStore = {
  async createAnalysis({
    name,
    description,
    author,
  }: {
    name: string;
    description?: string;
    author: string;
  }): Promise<SpectralWorkspaceSnapshot> {
    const snapshot = buildEmptyWorkspaceSnapshot({ name, description, author });

    const response = await fetch(buildUrl(''), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        analysisType: 'spectral_workspace',
        status: 'draft',
        snapshot,
      }),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Creazione analisi fallita: HTTP ${response.status} ${message}`
      );
    }

    const parsed = await parseSnapshotResponse(await response.json());
    if (!parsed) {
      throw new Error(
        'Risposta backend incompleta: snapshot spectral_workspace mancante.'
      );
    }

    return parsed;
  },

  async getAnalysis(id: string): Promise<SpectralWorkspaceSnapshot | null> {
    return fetchAnalysisById(id);
  },

  async updateAnalysis(
    id: string,
    snapshot: SpectralWorkspaceSnapshot
  ): Promise<SpectralWorkspaceSnapshot> {
    const payload = {
      ...snapshot,
      analysisId: id,
      updatedAt: new Date().toISOString(),
    };
    const requestBody = {
      analysisType: 'spectral_workspace',
      status: 'draft',
      snapshot: payload,
    };

    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Salvataggio analisi fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = parseSnapshotResponse(await response.json());
    if (!parsed) {
      throw new Error(
        'Risposta backend incompleta: snapshot spectral_workspace mancante.'
      );
    }

    return parsed;
  },

  async listAnalyses(): Promise<SpectralWorkspaceListItem[]> {
    const parsed = await fetchAllSpectralAnalyses();
    const workspaceItems = parsed.filter(isSpectralWorkspaceListItem);
    const listItems = await Promise.all(
      workspaceItems.map(item => resolveWorkspaceListItem(item))
    );

    return listItems
      .filter((item): item is SpectralWorkspaceListItem => item != null)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  },

  async listAnalysesForHub(): Promise<SpectralHubListItem[]> {
    const parsed = await fetchAllSpectralAnalyses();
    const entries = await Promise.all(
      parsed.map(async item => {
        if (item.analysisType === 'spectral_normalization') {
          return toLegacyHubItem(item);
        }

        const workspaceItem = await resolveWorkspaceListItem(item);

        if (workspaceItem) {
          return { kind: 'workspace' as const, ...workspaceItem };
        }

        return null;
      })
    );

    return entries
      .filter((item): item is SpectralHubListItem => item != null)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  },

  async listAnalysesUsingSessionGroup(
    groupId: string
  ): Promise<SessionGroupResearchUsage[]> {
    const items = await fetchAllSpectralAnalyses();
    const workspaceItems = items.filter(isSpectralWorkspaceListItem);
    const usages: SessionGroupResearchUsage[] = [];

    await Promise.all(
      workspaceItems.map(async item => {
        const snapshot = await resolveWorkspaceSnapshot(item);

        if (!snapshot || snapshot.activeSessionGroupId !== groupId) {
          return;
        }

        usages.push(toResearchUsage(item, snapshot));
      })
    );

    return usages.sort((left, right) =>
      right.updatedAt.localeCompare(left.updatedAt)
    );
  },

  async deleteAnalysis(id: string): Promise<void> {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.status === 404) {
      throw new Error('Analisi non trovata.');
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Eliminazione analisi fallita: HTTP ${response.status} ${message}`
      );
    }
  },
};
